// ── Date helpers ─────────────────────────────────────────────
const MONTHS_S = ['ian','feb','mar','apr','mai','iun','iul','aug','sep','oct','nov','dec'];

function fmtDateShort(s) {
  if (!s) return '';
  const d = new Date(s);
  return `${d.getDate()} ${MONTHS_S[d.getMonth()]}`;
}

export function daysDiff(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const d = new Date(dateStr); d.setHours(0,0,0,0);
  return Math.round((d - today) / 86400000);
}

function addDays(date, n)   { const d = new Date(date); d.setDate(d.getDate()+n); return d; }
function addMonths(date, n) { const d = new Date(date); d.setMonth(d.getMonth()+n); return d; }
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── Urgency color scale: verde → galben → portocaliu → rosu → negru ──
export function getUrgency(dateStr) {
  const n = daysDiff(dateStr);
  if (n === null) return { label: 'Fără termen', color: '#64748b', bg: 'rgba(100,116,139,.1)', order: 99 };
  if (n < 0)      return { label: `${Math.abs(n)}z depășit`, color: '#0f172a', bg: 'rgba(15,23,42,.09)', order: 0 };
  if (n === 0)    return { label: 'Azi',         color: '#dc2626', bg: 'rgba(220,38,38,.1)',  order: 1 };
  if (n <= 3)     return { label: `${n}z`,       color: '#dc2626', bg: 'rgba(220,38,38,.1)',  order: 2 };
  if (n <= 7)     return { label: `${n} zile`,   color: '#ea580c', bg: 'rgba(234,88,12,.1)',  order: 3 };
  if (n <= 30)    return { label: `${n} zile`,   color: '#ca8a04', bg: 'rgba(202,138,4,.1)',  order: 4 };
  return               { label: fmtDateShort(dateStr), color: '#16a34a', bg: 'rgba(22,163,74,.1)', order: 5 };
}

// ── Severity palette ──────────────────────────────────────────
export const SEV = {
  negru:      { color: '#0f172a', bg: 'rgba(15,23,42,.06)',   dot: '#475569', border: 'rgba(15,23,42,.15)',   label: 'Critic' },
  rosu:       { color: '#b91c1c', bg: 'rgba(220,38,38,.06)',  dot: '#ef4444', border: 'rgba(220,38,38,.25)',  label: 'Urgent' },
  portocaliu: { color: '#c2410c', bg: 'rgba(234,88,12,.06)',  dot: '#f97316', border: 'rgba(234,88,12,.25)',  label: 'Atenție' },
  galben:     { color: '#92400e', bg: 'rgba(202,138,4,.06)',  dot: '#f59e0b', border: 'rgba(202,138,4,.25)',  label: 'Urmărire' },
  verde:      { color: '#166534', bg: 'rgba(22,163,74,.06)',  dot: '#86efac', border: 'rgba(22,163,74,.25)',  label: 'Info' },
};

export const SEV_ORDER = { negru: 0, rosu: 1, portocaliu: 2, galben: 3, verde: 4 };

export const TIP_LABELS = {
  marca_osim: 'OSIM', marca_euipo: 'EUIPO', marca_wipo: 'WIPO',
  marca_internationala: 'Int.', idepot: 'I-Depot', dmi: 'Design', brevet: 'Brevet',
  brand: 'Marcă',
};

// ── Cycle logic ───────────────────────────────────────────────
// A renewal is "satisfied for this cycle" when:
//   reinnoire_depusa=true AND reinnoire_ciclu_expirare equals the current dataExpirare
// This means the filing was done specifically for this expiry period.
// When the user sets a new (future) dataExpirare after filing, ciclu_expirare no longer
// matches the new date, so the next cycle starts fresh.
export function cicluSatisfied(dataExpirare, reinnoire_depusa, reinnoire_ciclu_expirare) {
  return (
    !!reinnoire_depusa &&
    !!reinnoire_ciclu_expirare &&
    reinnoire_ciclu_expirare === dataExpirare
  );
}

// Whether we are in the active renewal alert window
export function inReinnoireWindow(dataExpirare) {
  const dl = daysDiff(dataExpirare);
  return dl !== null && dl >= -180 && dl <= 365;
}

// ── Renewal severity & label ──────────────────────────────────
function renewalSev(dl) {
  if (dl > 365 || dl < -180) return null;
  if (dl <= -150) return 'negru';
  if (dl < -90)   return 'negru';
  if (dl < 0)     return 'rosu';
  if (dl <= 30)   return 'rosu';
  if (dl <= 90)   return 'portocaliu';
  if (dl <= 180)  return 'galben';
  return 'verde';
}

function renewalLabel(dl) {
  if (dl <= -150) return `Perioadă de grație aproape de sfârșit (${Math.abs(dl)} zile depășite)`;
  if (dl < -90)   return `Expirată de ${Math.abs(dl)} zile — grație în curs`;
  if (dl < 0)     return `Expirată — reinnoire urgentă (${Math.abs(dl)}z depășit)`;
  if (dl <= 30)   return `Expiră în ${dl} zile — reinnoire necesară`;
  if (dl <= 90)   return `Expiră în ${Math.round(dl/30)} luni — inițiere reinnoire`;
  if (dl <= 180)  return `Expiră în ${Math.round(dl/30)} luni — poate fi reinnoită`;
  return `Reinnoire în ${Math.round(dl/30)} luni`;
}

// ── Tax alert helper (15-day window before deadline) ─────────
function taxAlert(id, label, deadlineDate, achitat, name, client, tip, procId, clientId, alerts) {
  if (achitat || !deadlineDate) return;
  const dl = daysDiff(toISO(deadlineDate));
  if (dl === null || dl > 15 || dl < -90) return;
  const sev = dl < 0 ? 'negru' : dl <= 7 ? 'rosu' : 'portocaliu';
  alerts.push({
    id, cat: 'taxa', sev,
    label: dl < 0 ? `${label} — depășită cu ${Math.abs(dl)} zile` : `${label} — scadentă în ${dl} zile`,
    name, client, date: toISO(deadlineDate), tip, procId, clientId,
  });
}

// ── Main alert engine ─────────────────────────────────────────
// proceduri: array from listAllProceduri (with tip_proc, data, client_name, client_id)
// brands: array of standalone brands (procedura_id === null), enriched with client_name
// contractePending: array from /contracts/pending/ (unsigned contracts)
export function buildAlerts(proceduri, brands = [], contractePending = []) {
  const alerts = [];

  for (const p of proceduri) {
    const d = p.data || {};
    const tip = p.tip_proc || p.tip;
    const client = p.client_name || '';
    const name = d.denumire || '';
    const cid = p.client_id;

    // ── Renewal alert (cycle-aware) ────────────────────────
    if (d.dataExpirare) {
      const dl = daysDiff(d.dataExpirare);
      const sat = cicluSatisfied(d.dataExpirare, d.reinnoire_depusa, d.reinnoire_ciclu_expirare);
      if (!sat) {
        const sev = renewalSev(dl);
        if (sev) alerts.push({ id: `ren-${p.id}`, cat: 'reinnoire', sev, label: renewalLabel(dl), name, client, date: d.dataExpirare, tip, procId: p.id, clientId: cid });
      }
    }

    // ── OSIM tax alerts ────────────────────────────────────
    if (tip === 'marca_osim' && d.depusa && d.dataDepunere) {
      const dep = new Date(d.dataDepunere); dep.setHours(0,0,0,0);

      // T1: depunere + inregistrare (deadline = ziua depunerii)
      taxAlert(`osim-t1-${p.id}`, 'Plată taxă depunere + înregistrare OSIM',
        dep, d.achitat_taxa_depunere_inreg, name, client, tip, p.id, cid, alerts);

      // T2: examinare (deadline = dataDepunere + 30 zile)
      taxAlert(`osim-t2-${p.id}`, 'Plată taxă examinare OSIM',
        addDays(dep, 30), d.achitat_taxa_examinare, name, client, tip, p.id, cid, alerts);

      // T3: eliberare certificat (deadline = BOPI + 2 luni + 30 zile)
      if (d.decizieDataBOPI) {
        const bopi = new Date(d.decizieDataBOPI); bopi.setHours(0,0,0,0);
        taxAlert(`osim-t3-${p.id}`, 'Plată taxă eliberare certificat OSIM',
          addDays(addMonths(bopi, 2), 30), d.certificatPrimit, name, client, tip, p.id, cid, alerts);
      }

      // T5: introducere mandatar (user-entered deadline)
      if (d.termenMandatar) {
        const dm = new Date(d.termenMandatar); dm.setHours(0,0,0,0);
        taxAlert(`osim-t5-${p.id}`, 'Plată taxă introducere mandatar OSIM',
          dm, d.achitat_taxa_mandatar, name, client, tip, p.id, cid, alerts);
      }

      // T6: modificare date titular (user-entered deadline)
      if (d.termenModificare) {
        const dm = new Date(d.termenModificare); dm.setHours(0,0,0,0);
        taxAlert(`osim-t6-${p.id}`, 'Plată taxă modificare date titular OSIM',
          dm, d.achitat_taxa_modificare, name, client, tip, p.id, cid, alerts);
      }
    }

    // ── OSIM: countdown publicare BOPI (7 zile de la depunere) ───
    // Shows as long as dataPrimaPublicare is empty; countdown goes negative indefinitely
    if (tip === 'marca_osim' && d.dataDepunere && d.achitat_taxa_depunere_inreg && !d.decizieDataBOPI) {
      const dep = new Date(d.dataDepunere); dep.setHours(0,0,0,0);
      const deadline = addDays(dep, 7);
      const dl = daysDiff(toISO(deadline));
      const sev = dl < -90 ? 'rosu' : dl < -30 ? 'portocaliu' : dl < 0 ? 'galben' : 'verde';
      alerts.push({
        id: `osim-pub-${p.id}`, cat: 'publicare', sev,
        label: 'Verifica publicarea in OSIM',
        name, client, date: toISO(deadline), tip, procId: p.id, clientId: cid,
      });
    }

    // ── EUIPO / WIPO: taxă de depunere neconfirmată ──────
    // Termen: dataDepunerii + 30 zile (EUIPO) / + 90 zile (WIPO).
    // Alerta rămâne până la bifarea "Marca a fost achitată".
    // Se ascunde dacă marca a avansat (publicată/înregistrată) — plata a avut
    // loc, deci nu inundăm homepage-ul cu marci vechi fără bifa completată.
    const platitAvansat = tip === 'marca_euipo'
      ? (d.dataPublicare || d.dataInregistrare || d.marcaInregistrata)
      : (d.dataPublicareGazette || d.dataTransmisWIPO || d.dataInregistrare || d.marcaInregistrata);
    if ((tip === 'marca_euipo' || tip === 'marca_wipo') && d.depusa && d.dataDepunere
        && !d.achitat_taxa_depunere && !platitAvansat) {
      const dep = new Date(d.dataDepunere); dep.setHours(0,0,0,0);
      const deadline = addDays(dep, tip === 'marca_euipo' ? 30 : 90);
      const dl = daysDiff(toISO(deadline));
      const sev = dl < -30 ? 'negru' : dl <= 7 ? 'rosu' : dl <= 30 ? 'portocaliu' : 'galben';
      alerts.push({
        id: `${tip}-achitat-${p.id}`, cat: 'taxa', sev,
        label: dl < 0
          ? `Plată taxă depunere ${TIP_LABELS[tip]} — depășită cu ${Math.abs(dl)} zile`
          : `Plată taxă depunere ${TIP_LABELS[tip]} — ${dl} zile rămase`,
        name, client, date: toISO(deadline), tip, procId: p.id, clientId: cid,
      });
    }
  }

  // ── Contract signature alerts ──────────────────────────
  for (const c of contractePending) {
    if (!c.data_contract) continue;
    const dep = new Date(c.data_contract); dep.setHours(0,0,0,0);
    const deadline = addDays(dep, 7);
    const dl = daysDiff(toISO(deadline));
    const sev = dl < -30 ? 'rosu' : dl < 0 ? 'portocaliu' : dl <= 3 ? 'galben' : 'verde';
    alerts.push({
      id: `contract-${c.id}`,
      cat: 'contract',
      sev,
      label: `Contractul ${c.company} nu a fost semnat`,
      name: '',
      client: c.client_name,
      date: toISO(deadline),
      tip: null,
      procId: null,
      clientId: c.client_id,
      contractId: c.id,
    });
  }

  // ── Standalone brands (no linked procedure) ────────────
  for (const brand of brands) {
    if (!brand.data_expirare) continue;
    const dl = daysDiff(brand.data_expirare);
    const sat = cicluSatisfied(
      brand.data_expirare,
      brand.reinnoire_depusa,
      brand.reinnoire_ciclu_expirare,
    );
    if (!sat) {
      const sev = renewalSev(dl);
      if (sev) alerts.push({
        id: `brand-ren-${brand.id}`,
        cat: 'reinnoire', sev,
        label: renewalLabel(dl),
        name: brand.name,
        client: brand.client_name || '',
        date: brand.data_expirare,
        tip: 'brand',
        procId: null,
        clientId: brand.client_id,
        brandId: brand.id,
      });
    }
  }

  return alerts.sort((a, b) =>
    (SEV_ORDER[a.sev] ?? 9) - (SEV_ORDER[b.sev] ?? 9) ||
    (a.date || '').localeCompare(b.date || '')
  );
}

// Get worst severity for a single procedure's data (for color dots)
export function getProcWorstSev(data, tip_proc) {
  if (!data) return null;
  const results = buildAlerts([{ id: 0, data, tip_proc, client_id: 0, client_name: '' }]);
  return results.length ? results[0].sev : null;
}
